// hooks/useTokenAllowance.js
import { useState, useCallback } from 'react';
import { useChainId, useConfig } from 'wagmi';
import { writeContract, waitForTransactionReceipt } from 'wagmi/actions';
import { parseEther } from 'viem';
import ERC20_ABI from '../../../utils/abis/erc20TokenABI.json';
import { publicClients } from '../../../lib/wagmi';
import { unifarmFactory } from '../../../utils/contract';

export const useTokenAllowance = () => {
    const [currentAllowance, setCurrentAllowance] = useState(null);
    const [isCheckingAllowance, setIsCheckingAllowance] = useState(false);
    const [isApprovingToken, setIsApprovingToken] = useState(false);
    const chainId = useChainId();
    const config = useConfig();

    const checkAllowance = useCallback(
        async (tokenAddress, ownerAddress) => {
            if (!chainId || !tokenAddress || !ownerAddress) return null;
            try {
                setIsCheckingAllowance(true);
                const allowance = await publicClients[chainId].readContract({
                    address: tokenAddress,
                    abi: ERC20_ABI,
                    functionName: "allowance",
                    args: [ownerAddress, unifarmFactory.address],
                });
                setCurrentAllowance(allowance);
                return allowance;
            } catch (error) {
                console.error("Error checking allowance:", error);
                setCurrentAllowance(null);
                return null;
            } finally {
                setIsCheckingAllowance(false);
            }
        },
        [chainId]
    );

    const approveToken = useCallback(
        async (tokenAddress, amount) => {
            try {
                setIsApprovingToken(true);
                const amountWei = parseEther(amount.toString());
                const hash = await writeContract(config, {
                    address: tokenAddress,
                    abi: ERC20_ABI,
                    functionName: "approve",
                    args: [unifarmFactory.address, amountWei],
                });
                const receipt = await waitForTransactionReceipt(config, { hash });
                setCurrentAllowance(amountWei);
                return { receipt, hash };
            } catch (error) {
                console.error("Error approving token:", error);
                throw error;
            } finally {
                setIsApprovingToken(false);
            }
        },
        [config]
    );

    const hasInsufficientAllowance = useCallback(
        (requiredAmount, allowanceToCheck = null) => {
            // Use provided allowance or fall back to state
            const allowanceValue = allowanceToCheck !== null ? allowanceToCheck : currentAllowance;
            if (!allowanceValue || !requiredAmount) return true;
            const requiredWei = parseEther(requiredAmount.toString());
            return allowanceValue < requiredWei;
        },
        [currentAllowance]
    );

    // Fixed: use the fresh allowance value directly
    const ensureAllowance = useCallback(
        async (tokenAddress, ownerAddress, requiredAmount) => {
            // Step 1: check allowance
            const allowance = await checkAllowance(tokenAddress, ownerAddress);
            if (allowance === null) {
                throw new Error("Failed to check token allowance");
            }

            // Step 2: approve if insufficient - use the fresh allowance value
            if (hasInsufficientAllowance(requiredAmount, allowance)) {
                const approvalResult = await approveToken(tokenAddress, 999999999);
                return approvalResult; // return approval receipt/hash
            }

            return { allowance }; // already sufficient
        },
        [checkAllowance, approveToken, hasInsufficientAllowance]
    );

    return {
        currentAllowance,
        isCheckingAllowance,
        isApprovingToken,
        checkAllowance,
        approveToken,
        hasInsufficientAllowance,
        ensureAllowance,
    };
};